import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { capitalizeFirstLetter, globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as staffService from "../../services/subAdmin.services";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_SIZE_ERROR = "Image size should not exceed 5 MB.";

const SubAdminEdit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [showdefault, setShowDefault] = useState({});
  const [saveType, setSaveType] = useState("");
  const [previewimage, setPreviewImage] = useState("");
  const [roleData, setRoleData] = useState([]);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "Sub Admins", url: "/admin/staff/list/1" },
    { title: t("btn_edit"), url: "" },
  ];

  useEffect(() => {
    staffService.Details(params.id).then((response) => {
      const data = response?.data || {};
      setShowDefault(data);
      setPreviewImage(data.profile_image);
    });
    staffService.RoleOptions().then((response) => {
      setRoleData(response?.data || []);
    });
  }, [params.id]);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    permission_role: Yup.mixed().required("Role is required"),
    files: Yup.mixed()
      .test("fileType", "Please select a valid image.", (value) => {
        if (!value || typeof value === "string") return true;
        const supportedFormats = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        return supportedFormats.includes(value.type);
      })
      .test("fileSize", IMAGE_SIZE_ERROR, (value) => {
        if (!value || typeof value === "string") return true;
        return value.size <= MAX_IMAGE_SIZE;
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: showdefault?.first_name || "",
      last_name: showdefault?.last_name || "",
      email: showdefault?.email || "",
      permission_role: showdefault?.permission_role?._id || showdefault?.permission_role || "",
      files: showdefault?.profile_image || "",
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      globalLoader(true);
      const formData = new FormData();
      formData.append("profile_image", values.files);
      formData.append("first_name", values.first_name);
      formData.append("last_name", values.last_name);
      formData.append("email", values.email);
      formData.append("permission_role", values.permission_role);
      formData.append("o_id", params.id);
      staffService.Edit(formData).then((response) => {
        setSubmitting(false);
        globalLoader(false);
        if (response.success) {
          Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          if (saveType !== "Save") navigate("/admin/staff/list/1");
        } else {
          Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
        }
      }).catch(() => { setSubmitting(false); globalLoader(false); });
    },
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <h6 className="main-content-label mb-3">Edit Sub Admin</h6>
                <div className="row row-sm">

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">First Name <span className="requirestar">*</span></label>
                    <input name="first_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.first_name} className="form-control" />
                    {formik.touched.first_name && formik.errors.first_name && <span className="text-danger d-flex">{formik.errors.first_name}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Last Name <span className="requirestar">*</span></label>
                    <input name="last_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.last_name} className="form-control" />
                    {formik.touched.last_name && formik.errors.last_name && <span className="text-danger d-flex">{formik.errors.last_name}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Email <span className="requirestar">*</span></label>
                    <input name="email" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.email} className="form-control" />
                    {formik.touched.email && formik.errors.email && <span className="text-danger d-flex">{formik.errors.email}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Role <span className="requirestar">*</span></label>
                    <div className="select-down-arrow">
                      <select name="permission_role" className="form-control" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.permission_role || ""}>
                        <option value="" disabled>Select Role</option>
                        {roleData.map((item) => (
                          <option key={item._id} value={item._id}>{capitalizeFirstLetter(item.title)}</option>
                        ))}
                      </select>
                    </div>
                    {formik.touched.permission_role && formik.errors.permission_role && <span className="text-danger d-flex">{formik.errors.permission_role}</span>}
                  </div>

                  <div className="col-md-6 form-group">
                    <label className="text-left d-flex">Profile Image</label>
                    <input className="form-control imgInput" name="files" accept="image/*" type="file"
                      onChange={(e) => {
                        formik.setFieldValue("files", e.currentTarget.files[0] || "");
                        if (e.currentTarget.files.length === 1) setPreviewImage(URL.createObjectURL(e.currentTarget.files[0]));
                        else setPreviewImage("");
                      }} />
                    {formik.touched.files && formik.errors.files && <span className="text-danger d-flex">{formik.errors.files}</span>}
                  </div>

                  {previewimage && <div className="mb-4"><img src={previewimage} style={{ height: "100px" }} alt="" /></div>}

                  <div className="mt-5">
                    <button onClick={() => setSaveType("Save")} className="btn btn-info mr-2" type="submit">{t("btn_save")}</button>
                    <button onClick={() => setSaveType("")} className="btn btn-success mr-2" type="submit">{t("btn_save_exit")}</button>
                    <button className="btn ripple btn-secondary" type="button" onClick={() => navigate("/admin/staff/list/1")}>{t("btn_cancel")}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default SubAdminEdit;
